import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SDKContext } from '../context/sdk';

type RoofFace = {
  panels: {
    roofFaceX: number | null;
    roofFaceY: number | null;
    width: number;
    height: number;
    isEnabled: boolean;
    enable: VoidFunction;
    disable: VoidFunction;
    remove: VoidFunction;
  }[];
};
enum PanelAction {
  Enable,
  Disable,
  Remove,
}

const PanelLayoutCanvas: React.FC<{ panels: RoofFace['panels'] }> = ({ panels }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const scale = 10;
    const canvas = canvasRef.current;

    if (canvas == null) return;
    const { min, max } = panels.reduce(
      (acc, panel) => {
        if (panel.roofFaceX == null || panel.roofFaceY == null) return acc;
        acc.min.x = Math.min(acc.min.x, panel.roofFaceX);
        acc.min.y = Math.min(acc.min.y, panel.roofFaceY);
        acc.max.x = Math.max(acc.max.x, panel.roofFaceX + panel.width);
        acc.max.y = Math.max(acc.max.y, panel.roofFaceY + panel.height);
        return acc;
      },
      {
        min: { x: Infinity, y: Infinity },
        max: { x: -Infinity, y: -Infinity },
      },
    );

    canvas.width = (max.x - min.x) * scale + 2;
    canvas.style.background = '#ddd';

    canvas.style.margin = '10px';
    canvas.height = (max.y - min.y) * scale + 2;
    const getX = (x: number) => (x - min.x) * scale;
    const getY = (y: number, height: number) => canvas.height - (y - min.y + height) * scale;

    const ctx = canvas.getContext('2d')!;
    panels.forEach((panel) => {
      if (panel.roofFaceX == null || panel.roofFaceY == null) return;
      ctx.fillStyle = panel.isEnabled ? 'rgba(0, 0, 0, 0.4)' : `rgba(0,0,0,0.2)`;
      ctx.strokeStyle = panel.isEnabled ? 'rgba(0, 0, 0, 1)' : `rgba(0,0,0,0.5)`;
      ctx.fillRect(
        getX(panel.roofFaceX),
        getY(panel.roofFaceY, panel.height),
        panel.width * scale,
        panel.height * scale,
      );
      ctx.strokeRect(
        getX(panel.roofFaceX),
        getY(panel.roofFaceY, panel.height),
        panel.width * scale,
        panel.height * scale,
      );
    });
  }, [panels]);
  return <canvas ref={canvasRef} />;
};
export const ModifySolarPanelsSection: React.FC = () => {
  const { design } = useContext(SDKContext);
  const layoutContainer = useRef<HTMLDivElement>(null);
  const [roofFaces, setRoofFaces] = useState<RoofFace[]>([]);
  const actOnFirstPanel = useCallback(
    (action: PanelAction) => () => {
      design.modifySolarPanels((layout: { roofFaces: RoofFace[] }) => {
        const firstPanel = layout.roofFaces
          .flatMap((roofFace) => roofFace.panels)
          .find((panel) => {
            if (action === PanelAction.Remove) return true;
            return action === PanelAction.Enable ? !panel.isEnabled : panel.isEnabled;
          });
        if (firstPanel == null) return;

        if (action === PanelAction.Enable) {
          firstPanel.enable();
        } else if (action === PanelAction.Disable) {
          firstPanel.disable();
        } else if (action === PanelAction.Remove) {
          firstPanel.remove();
        }
      });
    },
    [design],
  );
  const actOnAllPanels = useCallback(
    (action: PanelAction) => () => {
      design.modifySolarPanels((layout: { roofFaces: RoofFace[] }) => {
        layout.roofFaces.forEach((roofFace) => {
          roofFace.panels.forEach((panel) => {
            if (action === PanelAction.Enable) {
              panel.enable();
            } else if (action === PanelAction.Disable) {
              panel.disable();
            } else if (action === PanelAction.Remove) {
              panel.remove();
            }
          });
        });
      });
    },
    [design],
  );
  return (
    <div>
      <h2>Modify Solar Panels</h2>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
        <button onClick={actOnFirstPanel(PanelAction.Enable)}>Enable a panel</button>
        <button onClick={actOnFirstPanel(PanelAction.Disable)}>Disable a panel</button>
        <button onClick={actOnFirstPanel(PanelAction.Remove)}>Remove a panel</button>
        <button onClick={actOnAllPanels(PanelAction.Enable)}>Enable all panels</button>
        <button onClick={actOnAllPanels(PanelAction.Disable)}>Disable all panels</button>
        <button
          onClick={() => {
            design.modifySolarPanels((layout: { roofFaces: RoofFace[] }) =>
              setRoofFaces(layout.roofFaces),
            );
          }}
        >
          {roofFaces.length ? 'Refresh' : 'Show'} 2D panel layout
        </button>
      </div>
      <div ref={layoutContainer}>
        {roofFaces.map((roofFace, index) => (
          <PanelLayoutCanvas key={index} panels={roofFace.panels} />
        ))}
      </div>
    </div>
  );
};
